import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import {
  TradeCallbackLog,
  TradeErrorMessage,
} from "@services/models/Prime/GetTradeCallBackLogsModel";
import { ColDef } from "ag-grid-community";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ConstantFunctions from "../../utils/constants_functions";
import PrimeDetailsActionButton from "./components/PrimeDetailsActionButton";
import {
  getCanadaEventPillClass,
  getEuropeEventPillClass,
  getPhilippinesEventPillClass,
} from "./components/PrimeOrderPills";

interface PrimeLogsProps {
  tradeId?: string;
  instanceId?: string;
  endPoint?: string;
  sendCallbackEndPoint?: string;
  countryFilter?: string;
}

const constantFunctions = new ConstantFunctions();

const EVENT_PILL =
  "inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium";

const getLogEventPillClass = (
  eventId?: string | number | null,
  endPoint?: string,
) => {
  if (endPoint?.includes("EUR")) return getEuropeEventPillClass(eventId);
  if (endPoint?.includes("CAD")) return getCanadaEventPillClass(eventId);
  if (endPoint?.includes("PHP")) return getPhilippinesEventPillClass(eventId);

  const id = String(eventId ?? "");
  if (id === "4" || id === "6") return "bg-bg-status-green text-text-base";
  if (id === "9") return "bg-bg-status-red text-text-base";
  if (id === "5") return "bg-bg-status-yellow text-text-base";
  return "bg-bg-button-secondary text-text-base";
};

const hasRealTradeError = (
  errorMessage: TradeErrorMessage | string | null | undefined,
) => {
  if (!errorMessage) return false;

  if (typeof errorMessage === "string") {
    const trimmed = errorMessage.trim();
    return (
      trimmed.length > 0 && trimmed.toLowerCase() !== "null" && trimmed !== "{}"
    );
  }

  return Object.values(errorMessage).some((value) => {
    if (value == null || value === "") return false;
    if (typeof value === "object") {
      return Object.keys(value as object).length > 0;
    }
    return String(value).trim().length > 0;
  });
};

const PrimeLogs: React.FC<PrimeLogsProps> = ({
  tradeId,
  instanceId,
  endPoint,
  countryFilter,
}) => {
  const [logsData, setLogsData] = useState<TradeCallbackLog[]>([]);
  const [tradeErrorMessage, setTradeErrorMessage] =
    useState<TradeErrorMessage | null>(null);

  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [jsonDialogOpen, setJsonDialogOpen] = useState<boolean>(false);
  const [selectedJson, setSelectedJson] = useState<string>("");
  const [dialogTitle, setDialogTitle] = useState<string>("");

  /**
   * Fetch callback logs
   */
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(false);

      const response = await primeServices.getTradeCallbackLogs(
        {
          tradeId: tradeId ? parseInt(tradeId) : undefined,
          instanceId: instanceId || undefined,
        },
        endPoint || undefined,
      );

      setLogsData(response.data || []);
      setTradeErrorMessage(
        hasRealTradeError(response.tradeInfo?.errorMessage)
          ? (response.tradeInfo?.errorMessage ?? null)
          : null,
      );
    } catch (err) {
      console.error("Failed to fetch trade callback logs:", err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [tradeId, instanceId, endPoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Open JSON viewer
   */
  const handleJsonClick = useCallback((data: any, title: string) => {
    let formattedJson = "";

    try {
      formattedJson =
        typeof data === "string"
          ? JSON.stringify(JSON.parse(data), null, 2)
          : JSON.stringify(data, null, 2);
    } catch {
      formattedJson = String(data ?? "");
    }

    setSelectedJson(formattedJson);
    setDialogTitle(title);
    setJsonDialogOpen(true);
  }, []);

  /**
   * Table columns
   */
  const columnDefs: ColDef[] = useMemo(
    () => [
      /**
       * Date & Time
       */
      {
        headerName: "Date & Time",
        field: "createdAt",
        flex: 1.4,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",

        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <span className="text-[10px] text-text-base font-medium">
                - -
              </span>
            );
          }

          return (
            <div className="flex flex-col justify-center leading-tight">
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatDate(params.value)}
              </span>
              <span className="text-[10px] text-text-secondary">
                {constantFunctions.timeFormatInHHMMSS(params.value)}
              </span>
            </div>
          );
        },
      },

      /**
       * Event
       */
      {
        headerName: "Event",
        field: "eventId",
        flex: 0.75,
        minWidth: 70,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",

        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span
                className={`${EVENT_PILL} ${getLogEventPillClass(
                  params.value,
                  endPoint,
                )}`}
              >
                Event {params.value}
              </span>
            </div>
          );
        },
      },

      /**
       * Request
       */
      {
        headerName: "Req",
        field: "request",
        flex: 0.85,
        minWidth: 70,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,

        cellRenderer: (params: any) => {
          return (
            <div
              className="flex items-center h-full"
              onClick={(event) => event.stopPropagation()}
            >
              <PrimeDetailsActionButton
                label="View"
                icon="data_object"
                skipBusy
                onClick={() =>
                  handleJsonClick(
                    params.data.request,
                    `Request - Event ${params.data.eventId}`,
                  )
                }
              />
            </div>
          );
        },
      },

      /**
       * Response
       */
      {
        headerName: "Res",
        field: "response",
        flex: 0.85,
        minWidth: 70,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,

        cellRenderer: (params: any) => {
          return (
            <div
              className="flex items-center h-full"
              onClick={(event) => event.stopPropagation()}
            >
              <PrimeDetailsActionButton
                label="View"
                icon="data_object"
                skipBusy
                onClick={() =>
                  handleJsonClick(
                    params.data.response,
                    `Response - Event ${params.data.eventId}`,
                  )
                }
              />
            </div>
          );
        },
      },
    ],
    [endPoint, handleJsonClick],
  );

  if (error) {
    return (
      <div className="flex flex-col gap-[12px] mt-[20px]">
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Callback Logs:
          </p>
          <span className="text-text-base text-[12px] font-normal">
            Fetch error
          </span>
          <PrimeDetailsActionButton
            label="Try again"
            icon="refresh"
            onClick={fetchData}
            skipBusy
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="border border-border-subtle rounded-md bg-bg-base mt-[20px]">
        <Skeleton height={100} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px] overflow-auto"
      style={{ height: "65dvh" }}
    >
      {tradeErrorMessage && countryFilter === "BR" && (
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Trade Error:
          </p>
          {tradeErrorMessage.code && (
            <DataPill label={String(tradeErrorMessage.code)} tone="red" />
          )}
          <span className="text-text-base text-[12px] font-normal">
            An error occurred while processing this trade
          </span>
          <PrimeDetailsActionButton
            label="View JSON"
            icon="data_object"
            skipBusy
            onClick={() =>
              handleJsonClick(tradeErrorMessage, "Trade Error Message")
            }
          />
        </div>
      )}

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Callback Logs:
        </p>
        <div style={{ height: "400px", width: "100%" }}>
          {logsData.length > 0 ? (
            <AgGridReactTable
              rowData={logsData}
              columnDefs={columnDefs}
              getRowId={(params) => `log-${params.data.id}`}
              compact={true}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <span className="text-text-base text-[12px] font-normal">
                No data available
              </span>
            </div>
          )}
        </div>
      </div>

      <SlidingPanel
        isOpen={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        heading={dialogTitle}
        isSlider={true}
        widthDesktop={"34%"}
        showCross={false}
      >
        <div className="h-full flex flex-col pt-16">
          <div className="flex items-center justify-end px-[12px] py-[8px]">
            <CopyButton
              copyText={selectedJson}
              copyMessage="JSON copied!"
              buttonSize="28px"
              copyIconSize={14}
            />
          </div>
          <div className="flex-1 overflow-hidden px-[12px] pb-[12px]">
            <pre className="h-full bg-bg-base border border-border-subtle rounded-lg p-[12px] text-[12px] leading-relaxed text-text-base overflow-auto whitespace-pre-wrap break-all font-mono">
              {selectedJson}
            </pre>
          </div>
        </div>
      </SlidingPanel>
    </div>
  );
};

export default PrimeLogs;
